import type {
  ComponentLine,
  GeneralReqLine,
  ManpowerEntry,
  Quotation,
  QuotationTotals,
  ServiceLine,
} from '../../types/Quotation';

export function lineGeneralTotal(l: GeneralReqLine): number {
  return (l.unitPrice || 0) * (l.qty || 0);
}

// ─── EWT gross-up ────────────────────────────────────────────────────────────
// Expanded Withholding Tax buffer (IOCT quotations only — see Quotation.ewtPct).
// Combined multiplicatively with the existing markup layer so it rides along
// invisibly: never its own line, never its own label, just a bit more markup.
// Every call site that folds it in stays internally consistent (line items
// still sum to their printed section subtotal) because the same combined
// percentage is used everywhere that section's sell price is derived.
export function withEwt(basePct: number | undefined, ewtPct: number | undefined): number {
  return ((1 + (basePct || 0) / 100) * (1 + (ewtPct || 0) / 100) - 1) * 100;
}

// Manual (non-manpower) service lines store the amount the user actually
// typed — EWT is applied only at display/total time so the field stays
// reversible (zero out ewtPct and the printed figure reverts, nothing was
// overwritten). Per-line-pricing-from-manpower amounts already have EWT
// baked in when computed (see CalcsheetQuotationEditor's updateServiceRow),
// so don't pass this through those — it would double-apply.
export function serviceLineAmount(l: ServiceLine, ewtPct: number | undefined): number {
  return (l.amount || 0) * (1 + (ewtPct || 0) / 100);
}

// `ewtPct` (the quotation-wide default) is overridden by the line's own
// l.ewtPct when set — same resolution pattern as markup.
export function componentSellingUnit(l: ComponentLine, productMarkupPct: number, ewtPct: number = 0): number {
  const adjusted = componentCostUnit(l) * (1 + (l.contingencyPct || 0) / 100);
  const markup = l.markupPct != null ? l.markupPct : productMarkupPct;
  const ewt = l.ewtPct != null ? l.ewtPct : ewtPct;
  return adjusted * (1 + (markup || 0) / 100) * (1 + (ewt || 0) / 100);
}

export function componentCostUnit(l: ComponentLine): number {
  const base = (l.unitCost || 0) * (l.forex || 1);
  return base * (1 - (l.discountPct || 0) / 100);
}

export function componentWithContingencyUnit(l: ComponentLine): number {
  return componentCostUnit(l) * (1 + (l.contingencyPct || 0) / 100);
}

export function componentLineTotal(l: ComponentLine, productMarkupPct: number, ewtPct: number = 0): number {
  return componentSellingUnit(l, productMarkupPct, ewtPct) * (l.qty || 0);
}

export function componentLineCost(l: ComponentLine): number {
  return componentCostUnit(l) * (l.qty || 0);
}

export function componentLineWithContingency(l: ComponentLine): number {
  return componentWithContingencyUnit(l) * (l.qty || 0);
}

export function manpowerCost(m: ManpowerEntry): number {
  return (m.headcount || 0) * (m.mandays || 0) * ((m.dailyRate || 0) + (m.allowance || 0));
}

export function manpowerTotalCost(rows: ManpowerEntry[]): number {
  return rows.reduce((s, m) => s + manpowerCost(m), 0);
}

/** Daily cost of the whole team (ignoring mandays — used for per-line scope pricing). */
export function manpowerDailyRate(rows: ManpowerEntry[]): number {
  return rows.reduce((s, m) => s + (m.headcount || 0) * ((m.dailyRate || 0) + (m.allowance || 0)), 0);
}

export function servicesSubtotal(
  services: ServiceLine[],
  manpower: ManpowerEntry[],
  laborMarkupPct: number,
  fromManpower: boolean,
): { subtotal: number; cost: number } {
  if (fromManpower) {
    const cost = manpowerTotalCost(manpower);
    return { subtotal: cost * (1 + (laborMarkupPct || 0) / 100), cost };
  }
  const subtotal = services.reduce((s, l) => s + (l.amount || 0), 0);
  return { subtotal, cost: subtotal };
}

/** VAT rate actually applied: a Non-VAT quotation is always 0, whatever `vatPct` holds. */
export const effectiveVatPct = (q: { nonVat?: boolean; vatPct?: number }): number => (q.nonVat ? 0 : Number(q.vatPct) || 0);

export function computeTotals(q: Quotation): QuotationTotals {
  if (q.formulaVersion === 'legacy') {
    if (q.legacyTotalsSnapshot) return q.legacyTotalsSnapshot;
    return computeTotalsLegacy(q);
  }
  const generalReqtsQty = q.exportGeneralReqtsAsLot ? Math.max(1, q.generalReqtsExportQty || 1) : 1;
  const engineeringServicesQty = q.servicesFromManpower ? Math.max(1, q.engineeringServicesQty || 1) : 1;
  // IOCT-only pricing buffer, folded into every section's markup below —
  // never its own line. See Quotation.ewtPct and the `withEwt`/
  // `serviceLineAmount` helpers above.
  const ewtPct = q.ewtPct || 0;

  // General Requirements: cost → per-line or global markup. EWT rides on top
  // of the whole subtotal — General Requirements already prices markup only
  // at the subtotal level (per-line PDF rows show raw unitPrice × qty, not
  // the marked-up figure), so this stays consistent with the existing export.
  const generalReqtsUnitCost = q.generalReqts.reduce((s, l) => s + lineGeneralTotal(l), 0);
  const generalReqtsCost = generalReqtsUnitCost * generalReqtsQty;
  const generalReqtsWithContingency = generalReqtsCost;
  const hasPerLineGenMarkup = q.generalReqts.some(l => l.markupPct != null);
  const generalReqtsSubtotal = (
    hasPerLineGenMarkup
      ? q.generalReqts.reduce((s, l) => {
          const base = lineGeneralTotal(l);
          const markup = l.markupPct != null ? l.markupPct : (q.generalReqMarkupPct || 0);
          return s + base * (1 + markup / 100);
        }, 0) * generalReqtsQty
      : generalReqtsWithContingency * (1 + (q.generalReqMarkupPct || 0) / 100)
  ) * (1 + ewtPct / 100);

  // Components: raw cost → per-line contingency → markup → EWT. Passed as a
  // 3rd argument to componentSellingUnit/componentLineTotal so it applies
  // whether the line's markup came from the per-line override or the global
  // default — every per-line selling price/total shown anywhere (editor,
  // PDF, Excel) already includes it, so line items still sum to the printed
  // section subtotal.
  // Optional items are excluded from every contract figure (cost/subtotal/
  // grand total) and reported separately via componentsOptionalSubtotal.
  const contractComponents = q.components.filter((l) => !l.optional && !l.isHeader && !l.isChildHeader);
  const optionalComponents = q.components.filter((l) => l.optional && !l.isHeader && !l.isChildHeader);
  const componentsSubtotal = contractComponents.reduce(
    (s, l) => s + componentLineTotal(l, q.productMarkupPct, ewtPct),
    0,
  );
  const componentsCost = contractComponents.reduce((s, l) => s + componentLineCost(l), 0);
  const componentsWithContingency = contractComponents.reduce(
    (s, l) => s + componentLineWithContingency(l),
    0,
  );
  const componentsOptionalSubtotal = optionalComponents.reduce(
    (s, l) => s + componentLineTotal(l, q.productMarkupPct, ewtPct),
    0,
  );

  // Labor: manpower cost → markup. No contingency layer (rates carry their own buffer);
  // laborMarkupPct is the profit on engineering services.
  let laborCost: number;
  let servicesSub: number;
  let laborWithContingency: number;
  if (q.servicesFromManpower) {
    if (q.servicesPerLinePricing) {
      // Per-line pricing: each scope item stores its own amount (auto-computed
      // from QTY × Unit Price, or manually entered). laborCost is the raw cost
      // basis for margin calculation — QTY × the line's own Unit Price when
      // set, else the shared team daily rate (same fallback as the editor).
      // Per-line amounts already have EWT baked in at edit time (see
      // CalcsheetQuotationEditor's updateServiceRow), so sum them as-is here.
      const dailyRate = manpowerDailyRate(q.manpower);
      laborCost = q.services.reduce((s, l) => s + ((l.qty ?? l.days ?? 0) * (l.unitPrice ?? dailyRate)), 0);
      laborWithContingency = laborCost;
      servicesSub = q.services.reduce((s, l) => s + (l.amount || 0), 0);
    } else {
      laborCost = manpowerTotalCost(q.manpower) * engineeringServicesQty;
      laborWithContingency = laborCost;
      servicesSub = laborCost * (1 + (q.laborMarkupPct || 0) / 100) * (1 + ewtPct / 100);
    }
  } else {
    // Manual lump-sum lines: l.amount is the final price the user typed
    // (pre-EWT); serviceLineAmount folds EWT in at display/total time only,
    // same as the PDF/Excel per-line rendering, so line items still sum to
    // the printed subtotal.
    servicesSub = q.services.reduce((s, l) => s + serviceLineAmount(l, ewtPct), 0);
    laborCost = q.services.reduce((s, l) => s + (l.amount || 0), 0);
    laborWithContingency = laborCost;
  }

  const subtotal = generalReqtsSubtotal + componentsSubtotal + servicesSub;
  const discount = subtotal * ((q.discountPct || 0) / 100);
  const afterDiscount = subtotal - discount;

  // Delivery fee + minimum-order surcharge (opt-in). The ₱50k threshold is
  // tested against the goods+services subtotal (before delivery); the surcharge
  // applies independently of whether a base delivery fee was entered. Both are
  // VAT-able (added before VAT) and excluded from margin/budget.
  const deliveryEnabled = !!q.deliveryTermsEnabled;
  const minOrderThreshold = q.minOrderThreshold ?? 50000;
  const deliveryFee = deliveryEnabled ? (q.deliveryFee || 0) : 0;
  const smallOrderSurcharge = deliveryEnabled && subtotal < minOrderThreshold ? (q.smallOrderFee ?? 5000) : 0;
  const deliveryTotal = deliveryFee + smallOrderSurcharge;

  const vat = (afterDiscount + deliveryTotal) * (effectiveVatPct(q) / 100);
  const grandTotal = afterDiscount + deliveryTotal + vat;

  return {
    generalReqtsCost,
    generalReqtsWithContingency,
    generalReqtsSubtotal,
    componentsCost,
    componentsWithContingency,
    componentsSubtotal,
    componentsOptionalSubtotal,
    laborCost,
    laborWithContingency,
    servicesSubtotal: servicesSub,
    subtotal,
    discount,
    deliveryFee,
    smallOrderSurcharge,
    deliveryTotal,
    vat,
    grandTotal,
  };
}

// Legacy formulation matches the historical Excel templates:
//   Components: additive (1 + contingency - discount) instead of multiplicative
//   Labor: per-role contingency applied to each worker's unit price before summing
//   General Reqts: contingency is either applied or already baked into unit costs
function componentSellingUnitLegacy(l: ComponentLine, productMarkupPct: number): number {
  const base = (l.unitCost || 0) * (l.forex || 1);
  const adjusted = base * (1 + (l.contingencyPct || 0) / 100 - (l.discountPct || 0) / 100);
  return adjusted * (1 + (productMarkupPct || 0) / 100);
}

function componentCostUnitLegacy(l: ComponentLine): number {
  const base = (l.unitCost || 0) * (l.forex || 1);
  return base * (1 + (l.contingencyPct || 0) / 100 - (l.discountPct || 0) / 100);
}

function manpowerLineCostLegacy(m: ManpowerEntry, contPct: number): number {
  const unit = ((m.dailyRate || 0) + (m.allowance || 0)) * (1 + (contPct || 0) / 100);
  return (m.headcount || 0) * (m.mandays || 0) * unit;
}

export function computeTotalsLegacy(q: Quotation): QuotationTotals {
  const contPct = q.globalContingencyPct || 0;
  const cont = contPct / 100;
  const skipGenReqContingency = q.generalReqContingencyMode === 'baked';

  const generalReqtsCost = q.generalReqts.reduce((s, l) => s + lineGeneralTotal(l), 0);
  const generalReqtsWithContingency = skipGenReqContingency
    ? generalReqtsCost
    : generalReqtsCost * (1 + cont);
  const generalReqtsSubtotal =
    generalReqtsWithContingency * (1 + (q.generalReqMarkupPct || 0) / 100);

  const componentsCost = q.components.reduce(
    (s, l) => s + componentCostUnitLegacy(l) * (l.qty || 0),
    0,
  );
  const componentsSubtotal = q.components.reduce(
    (s, l) => s + componentSellingUnitLegacy(l, q.productMarkupPct) * (l.qty || 0),
    0,
  );

  let laborCost: number;
  let laborWithContingency: number;
  let servicesSub: number;
  if (q.servicesFromManpower) {
    laborCost = manpowerTotalCost(q.manpower);
    laborWithContingency = q.manpower.reduce((s, m) => s + manpowerLineCostLegacy(m, contPct), 0);
    servicesSub = laborWithContingency * (1 + (q.laborMarkupPct || 0) / 100);
  } else {
    servicesSub = q.services.reduce((s, l) => s + (l.amount || 0), 0);
    laborCost = servicesSub;
    laborWithContingency = servicesSub;
  }

  const subtotal = generalReqtsSubtotal + componentsSubtotal + servicesSub;
  const discount = subtotal * ((q.discountPct || 0) / 100);
  const afterDiscount = subtotal - discount;
  const vat = afterDiscount * (effectiveVatPct(q) / 100);
  const grandTotal = afterDiscount + vat;

  return {
    generalReqtsCost,
    generalReqtsWithContingency,
    generalReqtsSubtotal,
    componentsCost,
    componentsWithContingency: componentsCost,
    componentsSubtotal,
    laborCost,
    laborWithContingency,
    servicesSubtotal: servicesSub,
    subtotal,
    discount,
    vat,
    grandTotal,
  };
}

// IOCT gross margin from the quotation's snapshot.
// For non-legacy: cost fields are real, always returns a value.
// For legacy: returns null when costs weren't backfilled — those snapshots
// store cost=subtotal (placeholder), so margin would falsely read as zero.
export function ioctMargin(t: QuotationTotals): { value: number; pct: number } | null {
  const net = t.subtotal - t.discount;
  if (net <= 0) return null;
  const totalCost = t.generalReqtsCost + t.componentsCost + t.laborCost;
  const totalSubtotals = t.generalReqtsSubtotal + t.componentsSubtotal + t.servicesSubtotal;
  // Cost placeholder: totalCost equals sum of subtotals → no real cost data
  if (Math.abs(totalCost - totalSubtotals) < 0.01) return null;
  return { value: net - totalCost, pct: ((net - totalCost) / net) * 100 };
}

/** Spendable project budget from an IOCT quotation: VAT-ex value minus gross margin.
 *  When cost fields are real this equals total cost; when margin is unknown it
 *  falls back to the VAT-ex net (so a linked quotation still fills a budget). */
export function ioctCostBasis(t: QuotationTotals): number | null {
  const net = t.subtotal - t.discount;
  if (net <= 0) return null;
  const m = ioctMargin(t);
  return net - (m?.value ?? 0);
}

export const PHP = (n: number): string =>
  'PHP ' +
  (Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Number only — no "PHP" prefix. Used for line-item prices in the PDF. */
export const NUM = (n: number): string =>
  (Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const PCT = (n: number): string => `${(n || 0).toFixed(2)}%`;

/**
 * Client-facing discount % (no "%" suffix). Rounds to at most 2 decimals so
 * reverse-engineered peso targets don't print as 10.99243436% on PDFs.
 * Whole numbers stay whole: 10 → "10", 10.992… → "10.99".
 */
export function formatDiscountPct(pct: number): string {
  if (!Number.isFinite(pct)) return '0';
  const rounded = Math.round(pct * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return rounded.toFixed(2);
}

/** discountPct to store when the user picks an exact peso discount off a subtotal. */
export function discountPctFromAmount(subtotal: number, discountAmount: number): number {
  if (!Number.isFinite(subtotal) || subtotal <= 0) return 0;
  const amt = Math.min(Math.max(0, Number(discountAmount) || 0), subtotal);
  // discountPct is a PERCENT (computeTotals applies subtotal × pct / 100), so
  // scale the fraction by 100. Keep 8 decimals of a percent so the peso
  // round-trip stays within ¢1 even on nine-figure quotes.
  return Math.min(100, Math.max(0, Math.round((amt / subtotal) * 100 * 1e8) / 1e8));
}

/** discountPct to store when the user picks a target net total (VAT-ex). */
export function discountPctFromTargetNet(subtotal: number, targetNet: number): number {
  if (!Number.isFinite(subtotal) || subtotal <= 0) return 0;
  const target = Math.min(Math.max(0, Number(targetNet) || 0), subtotal);
  return discountPctFromAmount(subtotal, subtotal - target);
}
